import React from 'react';

// Export CSV (funcționează fără librării externe)
export function exportCSV(data, filename) {
  if (!data || !data.length) return;
  const keys = Object.keys(data[0]);
  const header = keys.join(',');
  const rows = data.map(r => keys.map(k => {
    const v = r[k] === null || r[k] === undefined ? '' : String(r[k]);
    return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g,'""')}"` : v;
  }).join(','));
  const csv = '\uFEFF' + [header, ...rows].join('\n'); // BOM pentru Excel
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename + '.csv'; a.click();
  URL.revokeObjectURL(url);
}

// Formateaza valorile numerice pentru afisare in print: 2 zecimale, separator de mii
// (format romanesc). Nu atinge sirurile care NU sunt pur numerice (date "2026-08-19",
// numere de inmatriculare "B-123-ABC", ore "14:23:00" etc.) - doar valori care sunt deja
// numar (typeof number) sau texte formate STRICT doar din cifre/virgula/minus.
function formateazaCelula(v) {
  if (v === null || v === undefined || v === '') return '';
  const esteNumeric = typeof v === 'number' || (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v.trim()));
  if (esteNumeric) {
    const n = typeof v === 'number' ? v : parseFloat(v);
    // v68: un numar intreg (nrCrt, kmPlecare, kmSosire...) se afiseaza fara zecimale;
    // doar o valoare cu adevarat fractionara (bani, distante cu virgula) pastreaza 2.
    const zecimale = Number.isInteger(n) ? 0 : 2;
    return n.toLocaleString('ro-RO', { minimumFractionDigits: zecimale, maximumFractionDigits: zecimale });
  }
  return String(v);
}

// Print tabel
export function printTable(title, data) {
  if (!data || !data.length) return;
  const keys = Object.keys(data[0]);
  const esteNumeric = (v) => typeof v === 'number' || (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v.trim()));
  const headers = keys.map(k => `<th style="padding:6px 8px;background:#1a3a5c;color:white;text-align:left;word-break:break-word">${k}</th>`).join('');
  const rows = data.map((r,i) => {
    const cells = keys.map(k => {
      const aliniere = esteNumeric(r[k]) ? 'right' : 'left';
      return `<td style="padding:5px 8px;border-bottom:1px solid #eee;text-align:${aliniere};word-break:break-word">${formateazaCelula(r[k])}</td>`;
    }).join('');
    return `<tr style="background:${i%2===0?'white':'#f5f8ff'}">${cells}</tr>`;
  }).join('');

  const html = `
    <html><head><title>${title}</title>
    <style>
      @page { size: A4 landscape; margin: 10mm; }
      body{font-family:Arial,sans-serif;font-size:11px}
      table{width:100%;border-collapse:collapse;table-layout:auto}
      th,td{max-width:220px;overflow-wrap:break-word}
      @media print{button{display:none}}
    </style>
    </head><body>
    <h2 style="color:#1a3a5c;margin-bottom:4px">${title}</h2>
    <p style="color:#888;margin-bottom:16px;font-size:11px">Generat: ${new Date().toLocaleString('ro-RO')}</p>
    <table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>
    <script>window.onload=()=>window.print()</script>
    </body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
}

// Print pentru foaia de parcurs CONSOLIDATĂ (o zi, un vehicul, un șofer) - format diferit
// de printTable (document cu antet + tabel de curse + total), nu un simplu tabel plat.
export function printFoaieConsolidata(grup) {
  // v76: in raportul tiparit cursele zilei apar cronologic (ora crescatoare); pe ecran ordinea e
  // "ultima salvata prima".
  const curseCronologic = [...grup.curse].sort((a, b) =>
    String(a.oraPlecare || '').localeCompare(String(b.oraPlecare || '')) || ((Number(a.nrCrt) || 0) - (Number(b.nrCrt) || 0)));
  const randuri = curseCronologic.map(c => {
    const km = (c.kmSosire != null && c.kmPlecare != null) ? (c.kmSosire - c.kmPlecare) : '';
    return `<tr>
      <td>${c.oraPlecare || ''} – ${c.oraSosire || ''}</td>
      <td>${c.traseu || ''}</td>
      <td style="text-align:right">${c.kmPlecare ?? ''}</td>
      <td style="text-align:right">${c.kmSosire ?? ''}</td>
      <td style="text-align:right"><b>${km}</b></td>
      <td>${c.scopDeplasarii || ''}</td>
    </tr>`;
  }).join('');

  const formatConsum = (v) => v == null ? '—' : v.toLocaleString('ro-RO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const html = `
    <html><head><title>Foaie de parcurs — ${grup.nrInmatriculare} — ${grup.data}</title>
    <style>
      @page { size: A4 portrait; margin: 14mm; }
      body{font-family:Arial,sans-serif;font-size:12px;color:#222}
      h2{color:#1a3a5c;margin:0 0 2px}
      .antet{display:flex;gap:24px;margin:10px 0 16px;padding:10px;background:#f5f8ff;border-radius:4px}
      .antet div b{display:block;color:#888;font-weight:normal;font-size:10px}
      table{width:100%;border-collapse:collapse;margin-bottom:14px}
      th{padding:6px 8px;background:#1a3a5c;color:white;text-align:left}
      td{padding:5px 8px;border-bottom:1px solid #eee}
      .total{display:flex;gap:24px;padding:10px;background:#f5f8ff;border-radius:4px;font-size:13px}
      .total b{color:#1a3a5c}
      @media print{button{display:none}}
    </style>
    </head><body>
    <h2>Foaie de parcurs — ${grup.data}</h2>
    <p style="color:#888;margin:0 0 10px;font-size:11px">Generat: ${new Date().toLocaleString('ro-RO')}</p>
    <div class="antet">
      <div><b>Vehicul</b>${grup.nrInmatriculare}</div>
      <div><b>Șofer</b>${grup.username}</div>
      <div><b>Data</b>${grup.data}</div>
    </div>
    <table><thead><tr>
      <th>Ora</th><th>Traseu</th><th>Km plecare</th><th>Km sosire</th><th>Km parcurși</th><th>Scopul deplasării</th>
    </tr></thead><tbody>${randuri}</tbody></table>
    <div class="total">
      <div><b>Km total zi:</b> ${grup.kmTotal ?? '—'}</div>
      <div><b>Consum normat mediu:</b> ${formatConsum(grup.consumNormatMediu)} l/100km</div>
      <div><b>Consum efectiv mediu:</b> ${formatConsum(grup.consumEfectivMediu)} l/100km</div>
    </div>
    <script>window.onload=()=>window.print()</script>
    </body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
}

export default function ExportButton({ data, filename, label = 'Export' }) {
  return (
    <div style={{display:'inline-flex',gap:4}}>
      <button className="btn btn-sm" style={{background:'#217346',color:'white'}}
        onClick={() => exportCSV(data, filename)} title="Export CSV (Excel)">
        📊 CSV
      </button>
      <button className="btn btn-sm" style={{background:'#555',color:'white'}}
        onClick={() => printTable(label, data)} title="Printare">
        🖨️ Print
      </button>
    </div>
  );
}
